import { forwardRef } from 'react';
import {
  IdentityTypeEnum,
  IUltimateBeneficiary,
} from '../../../../models/onboarding.model';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import AppButton from '../../../../components/base/button';
import GoBack from '../../../../components/base/go-back';
import BaseCollapsible from '../../../../components/base/collapsible';
import SingleUltimateBeneficiary from '../components/single-ultimate-beneficiary';
import { useOnboardingStore } from '../../../../store';
import { emailRegex } from '../../../../constants/constant';
import { Formik, FormikProvider } from 'formik';
import onboardingValidationSchemas from '../../../../constants/schemas/onboarding';
import React from 'react';
import toast from 'react-hot-toast';

export const UltimateBeneficiaries = forwardRef(() => {
  const {
    singleUltimateBeneficiary,
    businessUltimateBeneficiaries,
    setAllUltimateBeneficiaries,
    createUbo,
    updateUbo,
    deleteUbo,
    setUltimateBeneficiaryByIndex,
  } = useOnboardingStore();

  const [isLoading, setIsLoading] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);

  const addBeneficiary = () => {
    setAllUltimateBeneficiaries([
      ...businessUltimateBeneficiaries,
      { ...singleUltimateBeneficiary },
    ]);
  };

  const toggleBeneficiaryOpen = (index: number) => {
    const updatedValues = businessUltimateBeneficiaries.map(
      (ben: IUltimateBeneficiary, benIndex: number) => {
        return {
          ...ben,
          open: benIndex === index ? !ben.open : ben.open,
        };
      },
    );
    setAllUltimateBeneficiaries(updatedValues);
  };

  const removeFromList = (index: number) => {
    const updatedValues = businessUltimateBeneficiaries.filter(
      (_, benIndex: number) => benIndex !== index,
    );
    setAllUltimateBeneficiaries(updatedValues);
  };

  const removeBeneficiary = async (index: number) => {
    setIsDeleting(true);
    const owners = businessUltimateBeneficiaries || [];
    const owner = owners[index];

    if (owner) {
      if (owner.id) {
        const response = await deleteUbo(owner.id);
        if (response && response.value.isSuccess) {
          toast.success(`Beneficiary deleted successfully`);
          removeFromList(index);
        } else {
          toast.error(`Unable to delete beneficiary`);
        }
      } else {
        removeFromList(index);
      }
    }
    setIsDeleting(false);
  };

  const disableProceed = (index: number) => {
    const owners = businessUltimateBeneficiaries || [];
    const owner = owners[index];

    if (!owner) return true;

    return (
      !owner.firstName ||
      !owner.lastName ||
      owner.birthDate === null ||
      !owner.email ||
      (owner.email && !emailRegex.test(owner.email)) ||
      !owner.phone ||
      !owner.streetLine1 ||
      !owner.city ||
      !owner.state ||
      !owner.postalCode ||
      (owner.postalCode && owner.postalCode.length !== 6) ||
      !owner.countryCode ||
      (owner.identityType === IdentityTypeEnum.Id_Number &&
        (!owner.identityNumberType || !owner.identityNumber)) ||
      (owner.identityType === IdentityTypeEnum.Id_Document &&
        (!owner.identityDocumentType ||
          (!owner.identityDocumentFrontId && !owner.identityDocumentBackId)))
    );
  };

  const uboHandler = async (index: number) => {
    const owners = businessUltimateBeneficiaries || [];
    const owner = owners[index];
    if (!owner) return;

    setIsLoading(true);
    let response;
    if (owner.id) {
      response = await updateUbo(index);
    } else {
      response = await createUbo(index);
    }

    if (response && response.isSuccess) {
      setUltimateBeneficiaryByIndex(
        {
          ...owner,
          id: response.value.id,
        },
        index,
      );
      toast.success(
        `Beneficiary ${owner.id ? 'updated' : 'created'} successfully`,
      );
    } else {
      toast.error(`Unable to ${owner.id ? 'update' : 'create'} beneficiary`);
    }
    setIsLoading(false);
  };

  return (
    <div className="space-y-6">
      {/* Title */}
      <div>
        <GoBack text="Go back" path="/start-kyb" />

        <h2 className="text-2xl font-bold text-[#111827]">
          Add Ultimate Beneficial Owners (UBOs)
        </h2>
        <p className="text-gray-text text-sm">
          Please provide details of the individuals who ultimately own or
          control your business. This is required for regulatory compliance.
        </p>
      </div>

      {/* Beneficiaries Fields */}
      {businessUltimateBeneficiaries.map(
        (field: IUltimateBeneficiary, index: number) => (
          <div key={index} className="flex items-start gap-6 justify-between">
            <div className="w-[90%]">
              <Formik
                initialValues={field}
                enableReinitialize
                validationSchema={onboardingValidationSchemas[2]} // your validation schema
                validateOnMount
                onSubmit={() => {}}
              >
                {(formik) => (
                  <FormikProvider value={formik}>
                    <BaseCollapsible
                      isOpen={field.open}
                      index={index}
                      label={
                        field.firstName && field.lastName
                          ? `${field.firstName} ${field.lastName}`
                          : `Beneficiary ${index + 1}`
                      }
                      toggle={() => toggleBeneficiaryOpen(index)}
                      remove={() => removeBeneficiary(index)}
                      loadingRemove={isDeleting}
                      addMore={addBeneficiary}
                      proceed={() => uboHandler(index)}
                      proceedText={`${field.id ? 'Update' : 'Save'} Beneficiary`}
                      disableProceed={disableProceed(index)}
                      loadingProceed={isLoading}
                      entityType="Beneficiary"
                    >
                      <SingleUltimateBeneficiary beneficiaryIndex={index} />
                    </BaseCollapsible>
                  </FormikProvider>
                )}
              </Formik>
            </div>
            <AppButton
              type="danger"
              label="Delete"
              icon={<FiTrash2 />}
              clicked={() => removeBeneficiary(index)}
              fullWidth={false}
              loading={isDeleting}
            />
          </div>
        ),
      )}

      {/* Add Beneficiary Button */}
      <AppButton
        type="gray"
        label="Add another beneficiary"
        icon={<FiPlus />}
        clicked={addBeneficiary}
      />
    </div>
  );
});
UltimateBeneficiaries.displayName = 'UltimateBeneficiaries';

export default UltimateBeneficiaries;
